import { useEffect } from 'react';
import { Alert, Button, InputNumber, Select, Spin, Switch } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useEWallets } from '@/hooks/useEWallets';
import { useCardTerminals } from '@/hooks/useCardTerminals';
import { formatVND } from '@/utils/format';
import { ROUTES } from '@/constants/routes';
import { MONEY_MAX, moneyFormatter, moneyParser, moneyInputKeyDownGuard } from '../utils/moneyInput';
import type { PaymentMethod } from '../types';

export interface NonCashPaymentSectionProps {
  method: Exclude<PaymentMethod, 'cash'>;
  amount: number;
  accountId?: string;
  onAccountChange: (id: string | undefined) => void;
  nonCashAmount?: number;
  /** undefined = trả 100% phi tiền mặt (switch off); number = split với tiền mặt (switch on). */
  onNonCashAmountChange: (v: number | undefined) => void;
  cashReceived?: number;
  onCashReceivedChange: (v: number) => void;
}

interface MethodMeta {
  label: string;
  /** Nhãn ngắn cho ô nhập số tiền (CK / Ví ĐT / Thẻ TD). */
  shortLabel: string;
  optionLabel: (a: Record<string, unknown>) => string;
  showQr: boolean;
}

const META: Record<Exclude<PaymentMethod, 'cash'>, MethodMeta> = {
  bank: {
    label: 'Ngân hàng',
    shortLabel: 'CK',
    optionLabel: (a) => `${a.bank_name} - ${a.account_number}`,
    showQr: true,
  },
  wallet: {
    label: 'Ví điện tử',
    shortLabel: 'Ví ĐT',
    optionLabel: (a) => `${a.provider} - ${a.account_number}`,
    showQr: true,
  },
  card: {
    label: 'Máy POS / Thẻ',
    shortLabel: 'Thẻ TD',
    optionLabel: (a) => `${a.acquirer} - ${a.terminal_id}`,
    showQr: false,
  },
};

const labelStyle: React.CSSProperties = { color: '#555' };
const valueStyle: React.CSSProperties = { fontWeight: 600, textAlign: 'right' };
const changeStyle: React.CSSProperties = { ...valueStyle, fontWeight: 700, color: '#52c41a' };

export function NonCashPaymentSection({
  method, amount, accountId, onAccountChange,
  nonCashAmount, onNonCashAmountChange, cashReceived, onCashReceivedChange,
}: NonCashPaymentSectionProps) {
  const navigate = useNavigate();
  const bank = useBankAccounts();
  const wallet = useEWallets();
  const card = useCardTerminals();
  const query = method === 'bank' ? bank : method === 'wallet' ? wallet : card;
  const accounts = (query.data ?? []) as unknown as Array<Record<string, unknown> & { _id: string; is_default?: boolean }>;
  const isLoading = query.isLoading;
  const meta = META[method];

  // Auto-chọn default khi chưa chọn.
  // IMPORTANT: onAccountChange (và các callback prop khác) PHẢI referentially stable
  // (ví dụ: được wrap bằng useCallback ở parent) để tránh effect re-run loop —
  // nhất quán với cách các POS tab setter được memoize.
  useEffect(() => {
    if (accountId || accounts.length === 0) return;
    const fallback = accounts.find((a) => a.is_default) ?? accounts[0];
    onAccountChange(fallback._id);
  }, [accounts, accountId, onAccountChange]);

  // Switch "Trả kèm tiền mặt": off = nonCashAmount undefined (trả 100% phi tiền mặt,
  // tự bám theo tổng khi đổi); on = nonCashAmount là số → hiện ô nhập split.
  const splitWithCash = nonCashAmount !== undefined;
  const effectiveNonCash = nonCashAmount ?? amount;
  const cashPortion = Math.max(0, amount - effectiveNonCash);
  const change = Math.max(0, (cashReceived ?? 0) - cashPortion);

  const toggleSplit = (checked: boolean) => {
    if (checked) {
      onNonCashAmountChange(amount); // khởi tạo = tổng, user giảm xuống để nhận tiền mặt
    } else {
      onNonCashAmountChange(undefined); // trả 100% phi tiền mặt
      onCashReceivedChange(0);
    }
  };

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: '12px 0' }}><Spin size="small" /></div>;
  }

  if (accounts.length === 0) {
    return (
      <Alert
        type="warning" showIcon
        message={`Chưa cài đặt ${meta.label}`}
        description="Hãy cài đặt để sử dụng phương thức này."
        action={<Button size="small" onClick={() => navigate(ROUTES.settings.business)}>Cài đặt</Button>}
      />
    );
  }

  const selected = accounts.find((a) => a._id === accountId) ?? accounts[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>{meta.label}</div>
        <Select
          style={{ width: '100%' }}
          value={selected._id}
          onChange={(v) => onAccountChange(v)}
          options={accounts.map((a) => ({ value: a._id, label: meta.optionLabel(a) }))}
        />
      </div>

      {meta.showQr ? (
        <Alert
          type="info" showIcon
          message="Mã QR tự động cần kết nối backend — hiện chưa khả dụng. Vui lòng thanh toán theo thông tin tài khoản đã chọn."
        />
      ) : (
        <Alert type="info" showIcon message="Quẹt thẻ trên máy POS đã chọn." />
      )}

      {/* Switch trả kèm tiền mặt */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={labelStyle}>Trả kèm tiền mặt</span>
        <Switch checked={splitWithCash} onChange={toggleSplit} />
      </div>

      {splitWithCash && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            <tr>
              <td style={{ ...labelStyle, paddingBottom: 8, width: '50%' }}>Số tiền {meta.shortLabel}:</td>
              <td style={{ paddingBottom: 8 }}>
                <InputNumber
                  style={{ width: '100%' }} min={0} max={amount}
                  value={effectiveNonCash} formatter={moneyFormatter} parser={moneyParser}
                  onKeyDown={moneyInputKeyDownGuard}
                  onChange={(v) => onNonCashAmountChange(v ?? 0)}
                />
              </td>
            </tr>
            <tr>
              <td style={labelStyle}>Còn lại tiền mặt:</td>
              <td style={valueStyle}>{formatVND(cashPortion)}</td>
            </tr>
            <tr>
              <td style={{ ...labelStyle, paddingTop: 8 }}>Tiền mặt khách đưa:</td>
              <td style={{ paddingTop: 8 }}>
                <InputNumber
                  style={{ width: '100%' }} min={0} max={MONEY_MAX}
                  value={cashReceived} placeholder="0"
                  formatter={moneyFormatter} parser={moneyParser}
                  onKeyDown={moneyInputKeyDownGuard}
                  onChange={(v) => onCashReceivedChange(v ?? 0)}
                />
              </td>
            </tr>
            <tr>
              <td style={labelStyle}>Tiền thừa trả khách:</td>
              <td style={changeStyle}>{formatVND(change)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
